import { ELEMENT_TYPES, LEAF_TYPES } from 'cg/components/documents/editor/slate/types'
import { v4 as uuidv4 } from 'uuid'

// Reconciles the rough trees produced by the markdown (remark-slate) and HTML
// (defaults `deserialize`) paste deserializers into nodes that satisfy the
// editor schema, so `normalizeNode` does not have to repair them afterwards.
//
// Markdown / HTML can only express a bounded set of blocks; rich blocks (api,
// embed, tabs, math, ...) arrive as exact JSON via the internal slate-clipboard
// path and are intentionally not handled here.

const MARK_KEYS = Object.values(LEAF_TYPES)

const HEADINGS = [ELEMENT_TYPES.header_one, ELEMENT_TYPES.header_two, ELEMENT_TYPES.header_three]
const LISTS = [ELEMENT_TYPES.unordered_list, ELEMENT_TYPES.ordered_list, ELEMENT_TYPES.check_list]
const LIST_ITEMS = [ELEMENT_TYPES.list_item, ELEMENT_TYPES.check_list_item]
const INLINE_TYPES = [ELEMENT_TYPES.link]

const BLOCK_CHILD_TYPES = new Set([
  ...LISTS,
  ...LIST_ITEMS,
  ...HEADINGS,
  ELEMENT_TYPES.quote,
  ELEMENT_TYPES.hint,
  ELEMENT_TYPES.hint_content,
  ELEMENT_TYPES.code_tabs,
  ELEMENT_TYPES.code_no_tabs,
  'code_block',
  ELEMENT_TYPES.image,
  ELEMENT_TYPES.horizontal_rule
])

const isBlockChild = node => node != null && node.type && BLOCK_CHILD_TYPES.has(node.type)

const isTextLeaf = node =>
  node != null && typeof node === 'object' && typeof node.text === 'string' && node.type === undefined

const itemTypeFor = listType =>
  listType === ELEMENT_TYPES.check_list ? ELEMENT_TYPES.check_list_item : ELEMENT_TYPES.list_item

const makeText = (node, parentType) => {
  const leaf = {
    text: node && typeof node.text === 'string' ? node.text : '',
    uuid: (node && node.uuid) || uuidv4(),
    parent: { type: parentType }
  }

  if (node) {
    for (const key of MARK_KEYS) {
      if (node[key] !== undefined) {
        leaf[key] = node[key]
      }
    }
  }

  return leaf
}

const extractText = node => {
  if (node == null) return ''
  if (typeof node === 'string') return node
  if (typeof node.text === 'string') return node.text
  if (Array.isArray(node.children)) return node.children.map(extractText).join('')
  return ''
}

const sanitizeLink = (node, parentType) => ({
  type: ELEMENT_TYPES.link,
  url: node.url || node.link || '',
  targetBlank: node.targetBlank !== undefined ? node.targetBlank : true,
  parent: { type: parentType },
  children: collectInlines(node.children, ELEMENT_TYPES.link)
})

// Flatten an arbitrary subtree into a list of inline nodes (text leaves + links).
function collectInlines(nodes, parentType) {
  const out = []

  const walk = arr => {
    for (const n of arr || []) {
      if (typeof n === 'string') {
        out.push(makeText({ text: n }, parentType))
      } else if (isTextLeaf(n)) {
        out.push(makeText(n, parentType))
      } else if (n && n.type && INLINE_TYPES.includes(n.type)) {
        out.push(sanitizeLink(n, parentType))
      } else if (n && Array.isArray(n.children)) {
        walk(n.children)
      }
    }
  }

  walk(nodes)

  if (out.length === 0) {
    out.push(makeText({ text: '' }, parentType))
  }

  return out
}

const buildCodeTabs = (text, language = 'plaintext') => {
  const tabId = `code-${uuidv4()}-1`
  const lines = String(text == null ? '' : text).split(/\r\n|\r|\n/)

  if (lines.length === 0) {
    lines.push('')
  }

  return {
    id: `code-${uuidv4()}`,
    type: ELEMENT_TYPES.code_tabs,
    defaultActiveTab: tabId,
    parent: { type: null },
    children: [
      {
        type: ELEMENT_TYPES.code_tabs_header,
        parent: { type: ELEMENT_TYPES.code_tabs },
        children: [
          {
            type: ELEMENT_TYPES.code_tabs_header_item,
            tabId,
            parent: { type: ELEMENT_TYPES.code_tabs_header },
            children: [{ uuid: uuidv4(), text: 'Title', parent: { type: ELEMENT_TYPES.code_tabs_header_item } }]
          }
        ]
      },
      {
        type: ELEMENT_TYPES.code_tabs_content_item,
        tabId,
        language,
        parent: { type: ELEMENT_TYPES.code_tabs },
        children: lines.map(line => ({
          type: ELEMENT_TYPES.code_tabs_content_item_line,
          parent: { type: ELEMENT_TYPES.code_tabs_content_item },
          children: [{ uuid: uuidv4(), text: line, parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
        }))
      }
    ]
  }
}

const sanitizeListItem = (node, listType) => {
  const itemType = itemTypeFor(listType)
  const inlineChildren = []

  for (const child of node.children || []) {
    if (child && LISTS.includes(child.type)) {
      // Nested lists are lifted by the caller (sanitizeList).
      continue
    }

    if (typeof child === 'string') {
      inlineChildren.push(makeText({ text: child }, itemType))
    } else if (isTextLeaf(child)) {
      inlineChildren.push(makeText(child, itemType))
    } else if (child && child.type && INLINE_TYPES.includes(child.type)) {
      inlineChildren.push(sanitizeLink(child, itemType))
    } else if (child && Array.isArray(child.children)) {
      for (const leaf of collectInlines(child.children, itemType)) {
        inlineChildren.push(leaf)
      }
    }
  }

  if (inlineChildren.length === 0) {
    inlineChildren.push(makeText({ text: '' }, itemType))
  }

  return { type: itemType, parent: { type: listType }, children: inlineChildren }
}

const sanitizeList = (node, parentType) => {
  const listType = LISTS.includes(node.type) ? node.type : ELEMENT_TYPES.unordered_list
  const items = []

  // A nested list is kept intact as a sibling of the list item (parent === list),
  // which is how the editor renders/indents nested lists.
  const pushNested = child => {
    items.push(sanitizeList(child, listType))
  }

  for (const child of node.children || []) {
    if (child && LIST_ITEMS.includes(child.type)) {
      items.push(sanitizeListItem(child, listType))
      for (const grand of child.children || []) {
        if (grand && LISTS.includes(grand.type)) {
          pushNested(grand)
        }
      }
    } else if (child && LISTS.includes(child.type)) {
      pushNested(child)
    } else if (child != null) {
      items.push({
        type: itemTypeFor(listType),
        parent: { type: listType },
        children: collectInlines([child], itemTypeFor(listType))
      })
    }
  }

  if (items.length === 0) {
    items.push({
      type: itemTypeFor(listType),
      parent: { type: listType },
      children: [makeText({ text: '' }, itemTypeFor(listType))]
    })
  }

  return { type: listType, parent: { type: parentType }, children: items }
}

const sanitizeQuote = (node, parentType) => {
  const contentBlocks = []
  let pending = []

  const flushPending = () => {
    if (pending.length) {
      contentBlocks.push({
        type: ELEMENT_TYPES.default,
        parent: { type: ELEMENT_TYPES.quote_content },
        children: pending
      })
      pending = []
    }
  }

  const flat = []
  for (const child of node.children || []) {
    if (child && child.type === ELEMENT_TYPES.quote_content) {
      flat.push(...(child.children || []))
    } else {
      flat.push(child)
    }
  }

  for (const child of flat) {
    if (typeof child === 'string') {
      pending.push(makeText({ text: child }, ELEMENT_TYPES.default))
    } else if (isTextLeaf(child)) {
      pending.push(makeText(child, ELEMENT_TYPES.default))
    } else if (child && child.type && INLINE_TYPES.includes(child.type)) {
      pending.push(sanitizeLink(child, ELEMENT_TYPES.default))
    } else if (child && Array.isArray(child.children)) {
      flushPending()
      contentBlocks.push({
        type: ELEMENT_TYPES.default,
        parent: { type: ELEMENT_TYPES.quote_content },
        children: collectInlines(child.children, ELEMENT_TYPES.default)
      })
    }
  }

  flushPending()

  if (contentBlocks.length === 0) {
    contentBlocks.push({
      type: ELEMENT_TYPES.default,
      parent: { type: ELEMENT_TYPES.quote_content },
      children: [makeText({ text: '' }, ELEMENT_TYPES.default)]
    })
  }

  return {
    type: ELEMENT_TYPES.quote,
    parent: { type: parentType },
    children: [
      {
        type: ELEMENT_TYPES.quote_content,
        parent: { type: ELEMENT_TYPES.quote },
        children: contentBlocks
      }
    ]
  }
}

const paragraphFromInlines = (inlines, parentType) => ({
  type: ELEMENT_TYPES.default,
  parent: { type: parentType },
  children: inlines
})

// Returns an array of schema-valid block nodes for a single rough node.
const sanitizeBlock = (node, parentType) => {
  if (node == null) return []

  if (typeof node === 'string') {
    if (node.trim() === '') return []
    return [paragraphFromInlines([makeText({ text: node }, ELEMENT_TYPES.default)], parentType)]
  }

  if (isTextLeaf(node)) {
    return [paragraphFromInlines([makeText(node, ELEMENT_TYPES.default)], parentType)]
  }

  const type = node.type

  if (INLINE_TYPES.includes(type)) {
    return [paragraphFromInlines([sanitizeLink(node, ELEMENT_TYPES.default)], parentType)]
  }

  if (type === ELEMENT_TYPES.default || type === 'paragraph' || HEADINGS.includes(type)) {
    // Lift any block children (e.g. an image inside a paragraph) out as siblings,
    // grouping consecutive inline content into blocks of this type.
    const out = []
    let run = []

    const flushRun = () => {
      if (run.length) {
        out.push({ type, parent: { type: parentType }, children: collectInlines(run, type) })
        run = []
      }
    }

    for (const child of node.children || []) {
      if (isBlockChild(child)) {
        flushRun()
        out.push(...sanitizeBlock(child, parentType))
      } else {
        run.push(child)
      }
    }

    flushRun()

    if (out.length === 0) {
      out.push({ type, parent: { type: parentType }, children: [makeText({ text: '' }, type)] })
    }

    return out
  }

  if (LISTS.includes(type)) {
    return [sanitizeList(node, parentType)]
  }

  if (LIST_ITEMS.includes(type)) {
    return [sanitizeList({ type: ELEMENT_TYPES.unordered_list, children: [node] }, parentType)]
  }

  if (type === ELEMENT_TYPES.quote) {
    return [sanitizeQuote(node, parentType)]
  }

  if (type === ELEMENT_TYPES.hint) {
    return [{ ...node, parent: { type: parentType } }]
  }

  if (type === ELEMENT_TYPES.code_tabs) {
    return [{ ...node, parent: { type: parentType } }]
  }

  if (type === ELEMENT_TYPES.code_no_tabs || type === 'code_block') {
    return [buildCodeTabs(extractText(node), node.language || 'plaintext')]
  }

  if (type === ELEMENT_TYPES.image) {
    return [
      {
        ...node,
        url: node.url || node.link || node.src || '',
        caption: node.caption != null ? node.caption : node.alt || '',
        size: node.size != null ? node.size : 100,
        parent: { type: parentType },
        children: [makeText({ text: '' }, ELEMENT_TYPES.image)]
      }
    ]
  }

  if (type === ELEMENT_TYPES.horizontal_rule) {
    return [
      { type, parent: { type: parentType }, children: [makeText({ text: '' }, ELEMENT_TYPES.horizontal_rule)] }
    ]
  }

  // Unknown container (e.g. <div>/<span> wrappers): unwrap children as blocks.
  if (Array.isArray(node.children)) {
    return node.children.flatMap(child => sanitizeBlock(child, parentType))
  }

  return []
}

// Public entry: takes the deserializer output (array of rough nodes) and returns
// an array of schema-valid top-level blocks.
export const sanitizePastedFragment = nodes => {
  const input = Array.isArray(nodes) ? nodes : [nodes]
  const out = []

  for (const node of input) {
    out.push(...sanitizeBlock(node, null))
  }

  if (out.length === 0) {
    out.push(paragraphFromInlines([makeText({ text: '' }, null)], null))
  }

  return out
}

export default sanitizePastedFragment
